import { usePathname, useRouter } from 'next/navigation';
import usePost from './usePost';
import { toast } from 'react-hot-toast';
import { useState } from 'react';


interface CartItemLoadingState {
  [productId: string]: boolean;
}




const useCheckout = () => {
  const [cart, setCart] = useState<any>(null);
  const { PostMethod } = usePost();
  const [cartLoading, setCartLoading] = useState<CartItemLoadingState>({});
  const pathname = usePathname();
  const router = useRouter();
  const moveCart = async (productId: string, type: string = 'cart', qty: number | string = '') => {
    const CID = sessionStorage.getItem("CID");
    const CID_TIMESTAMP = sessionStorage.getItem("CID_TIMESTAMP");

    // Check if CID is expired (older than 1 hour)
    const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds
    const isExpired = CID_TIMESTAMP && (Date.now() - parseInt(CID_TIMESTAMP)) > ONE_HOUR;

    if (isExpired) {
      // Clear expired CID
      sessionStorage.removeItem("CID");
      sessionStorage.removeItem("CID_TIMESTAMP");
    }

    // build request body safely
    const data: Record<string, any> = {};
    // Only reuse an existing checkout CID for the "Buy Now" flow
    // (type=product), so successive Buy Nows can merge into one
    // checkout. For the cart-to-checkout flow (type=cart), always
    // start fresh — otherwise a stale CID from a previous Buy Now
    // makes the backend return that old checkout's contents instead
    // of the current cart, and the user lands on /checkout/<oldId>
    // with the wrong products and total.
    if (type === 'product' && CID != null && CID !== '' && !isExpired) {
      data.CID = CID;
    }
    if (qty != null) { // allow 0, exclude only null/undefined
      data.qty = qty;
    }

    try {
      let response = await PostMethod({
        url: `mainCart/add-cart/${productId}?type=${type}`,
        method: 'post',
        body: data,
      });

      if (response?.success === true) {
        return response
      }
      return response
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const getCheckoutCartList = async () => {
    try {
      let response = await PostMethod({
        url: `mainCart/get-cart/${pathname.split('/')[3]}`,
        method: 'get',
        language: pathname.split('/')[1],
      });

      if (response?.success === true) {
        if (response?.data?.items?.length === 0) {
          router.back();
          return;
        }
        setCart(response.data);
      } else {
        router.back();
        setCart(null);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const setProductLoading = (productId: string, isLoading: boolean) => {
    setCartLoading((prev) => ({ ...prev, [productId]: isLoading }));
  };

  const handleAddressCheckout = async (address: any) => {

    try {
      let response = await PostMethod({
        url: `order/address/${pathname.split('/')[3]}`,
        body: { ...address },
        method: 'post',
      });

      if (response?.success === true) {
        // await getCheckoutCartList();
      } else {
        toast.error(response?.message || 'Failed to Add Address.');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to Add Address.');
    }
  };

  const handlePaymentCheckout = async (body: any, language = "en") => {

    try {
      let response = await PostMethod({
        url: `order/createOrder/${pathname.split('/')[3]}`,
        body: { ...body },
        method: 'post',
        language
      });

      return response
    } catch (error) {

      return error
    }
  };

  const addCheckoutCartList = async (productId: string) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: `mainCart/add-product/${pathname.split('/')[3]}`,
        body: { productId },
        method: 'put',
      });

      if (response?.success === true) {
        await getCheckoutCartList();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Failed to add to cart.');
      }
    } catch (error) {
      toast.error('An error occurred while adding to the cart.');
    } finally {
      setProductLoading(productId, false);
    }
  };

  const removeCheckoutCartList = async (productId: string) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: `mainCart/remove-cart/${pathname.split('/')[3]}`,
        body: { productId },
        method: 'put',
      });

      if (response?.success === true) {
        await getCheckoutCartList();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Failed to remove from cart.');
      }
    } catch (error) {
      toast.error('An error occurred while removing from the cart.');
    } finally {
      setProductLoading(productId, false);
    }
  };
  const deleteCheckoutCartList = async (productId: string) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: `mainCart/delete-cart/${productId}?cart=${pathname.split('/')[3]}`,
        method: 'delete',
      });

      if (response?.success === true) {
        await getCheckoutCartList();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Failed to remove from cart.');
      }
    } catch (error) {
      toast.error('An error occurred while removing from the cart.');
    } finally {
      setProductLoading(productId, false);
    }
  };



  return {
    cartLoading,
    cart,
    moveCart,
    handleAddressCheckout,
    getCheckoutCartList,
    addCheckoutCartList,
    handlePaymentCheckout,
    removeCheckoutCartList,
    deleteCheckoutCartList
  };
};

export default useCheckout;
